import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for simulating data tag values based on topic path names.
 * Generates realistic values that update on random intervals.
 * 
 * When tagConfigs are provided, tags with a simulationConfig will use that
 * config for value generation (type, min/max, states, interval). Tags without
 * simulationConfig fall back to keyword-based detection from the topic path.
 * 
 * @param {string[]} topicPaths - Array of topic paths to simulate
 * @param {Object[]} [tagConfigs=[]] - Optional array of tag config objects (each may have .simulationConfig)
 * @returns {Object} - { values: { [topicPath]: value }, recentlyChanged: Set }
 */
const useSimulatedData = (topicPaths = [], tagConfigs = []) => {
  const [values, setValues] = useState({});
  const [recentlyChanged, setRecentlyChanged] = useState(new Set());
  const countersRef = useRef({});
  const baseValuesRef = useRef({});
  const timersRef = useRef({});
  const highlightTimersRef = useRef({});
  const mountedRef = useRef(true);

  // Serialize to detect real changes (not just reference changes)
  const topicPathsKey = JSON.stringify(topicPaths);
  const tagConfigsKey = JSON.stringify(tagConfigs.map(t => ({ id: t?.id, topicPath: t?.topicPath, simulationConfig: t?.simulationConfig })));

  // Build a lookup map from key (topicPath or tag id) -> simulationConfig
  const simConfigMap = useRef({});
  useEffect(() => {
    const map = {};
    tagConfigs.forEach((tag) => {
      const key = tag?.topicPath || tag?.id;
      if (key && tag?.simulationConfig) {
        map[key] = tag.simulationConfig;
      }
    });
    simConfigMap.current = map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagConfigsKey]);

  // Generate a value from explicit simulationConfig
  const generateFromConfig = useCallback((topicPath, config) => {
    switch (config.type) {
      case 'numeric': {
        const min = config.min ?? 0;
        const max = config.max ?? 100;
        const value = min + Math.random() * (max - min);
        return parseFloat(value.toFixed(2));
      }
      case 'counter': {
        const min = config.min ?? 0;
        const max = config.max ?? 3;
        if (!countersRef.current[topicPath]) {
          countersRef.current[topicPath] = Math.floor(Math.random() * 1000);
        }
        countersRef.current[topicPath] += Math.floor(Math.random() * (max - min + 1)) + (min ?? 1);
        return countersRef.current[topicPath];
      }
      case 'state': {
        const states = config.states?.length > 0
          ? config.states
          : ['Running', 'Idle', 'Paused'];
        return states[Math.floor(Math.random() * states.length)];
      }
      case 'boolean':
        return Math.random() > 0.5;
      default:
        return parseFloat((Math.random() * 100).toFixed(2));
    }
  }, []);

  // Generate a value based on topic path keywords (original fallback logic)
  const generateValue = useCallback((topicPath) => {
    // Check for explicit simulation config first
    const config = simConfigMap.current[topicPath];
    if (config) {
      return generateFromConfig(topicPath, config);
    }

    const path = topicPath.toLowerCase();
    
    // Temperature values (68-85°F with small fluctuations)
    if (path.includes('temperature') || path.includes('temp')) {
      const base = 72 + Math.random() * 8;
      return parseFloat(base.toFixed(1));
    }
    
    // Counter/count values (incrementing integers)
    if (path.includes('count') || path.includes('counter') || path.includes('partcounter')) {
      if (!countersRef.current[topicPath]) {
        countersRef.current[topicPath] = Math.floor(Math.random() * 1000);
      }
      countersRef.current[topicPath] += Math.floor(Math.random() * 3) + 1;
      return countersRef.current[topicPath];
    }
    
    // Pressure values (around 14.7 PSI with small fluctuations)
    if (path.includes('pressure')) {
      const base = 14.7 + (Math.random() - 0.5) * 2;
      return parseFloat(base.toFixed(2));
    }
    
    // Speed/rate values (0-100)
    if (path.includes('speed') || path.includes('rate') || path.includes('feed')) {
      return Math.floor(Math.random() * 100);
    }
    
    // Override values (percentage 80-120)
    if (path.includes('override')) {
      return Math.floor(80 + Math.random() * 40);
    }
    
    // State/status values (cycling through states)
    if (path.includes('state') || path.includes('status')) {
      const states = ['Running', 'Idle', 'Paused', 'Active'];
      return states[Math.floor(Math.random() * states.length)];
    }
    
    // E-stop values (boolean, mostly false)
    if (path.includes('estop') || path.includes('emergency')) {
      return Math.random() > 0.95; // 5% chance of being true
    }
    
    // Weight/scale values
    if (path.includes('weight') || path.includes('scale')) {
      const base = 100 + Math.random() * 50;
      return parseFloat(base.toFixed(2));
    }
    
    // Time/duration values (in seconds or minutes)
    if (path.includes('time') || path.includes('duration') || path.includes('operationtime')) {
      return Math.floor(Math.random() * 3600); // 0-3600 seconds
    }
    
    // Torque values
    if (path.includes('torque')) {
      const base = 10 + Math.random() * 40;
      return parseFloat(base.toFixed(1));
    }
    
    // Efficiency/OEE/percentage values - these change very slowly and minimally
    if (path.includes('efficiency') || path.includes('oee') || path.includes('percent')) {
      // Initialize a stable base value if we don't have one
      if (!baseValuesRef.current[topicPath]) {
        baseValuesRef.current[topicPath] = 82 + Math.random() * 10; // Base between 82-92%
      }
      // Tiny fluctuation: +/- 0.3% from base
      const fluctuation = (Math.random() - 0.5) * 0.6;
      const newValue = baseValuesRef.current[topicPath] + fluctuation;
      // Clamp between 75-98%
      return parseFloat(Math.max(75, Math.min(98, newValue)).toFixed(1));
    }
    
    // Flow values
    if (path.includes('flow')) {
      const base = 5 + Math.random() * 10;
      return parseFloat(base.toFixed(2));
    }
    
    // Lot number values (e.g., "L01-1370", "L03-1100")
    if (path.includes('lot')) {
      const lineNum = String(Math.floor(Math.random() * 5) + 1).padStart(2, '0');
      const batchNum = String(Math.floor(Math.random() * 900) + 1000);
      return `L${lineNum}-${batchNum}`;
    }
    
    // Position/location values
    if (path.includes('position') || path.includes('location') || path.includes('zerokey')) {
      return {
        x: parseFloat((Math.random() * 100).toFixed(2)),
        y: parseFloat((Math.random() * 100).toFixed(2)),
        z: parseFloat((Math.random() * 10).toFixed(2)),
      };
    }
    
    // Default: random numeric value
    const base = Math.random() * 100;
    return parseFloat(base.toFixed(2));
  }, [generateFromConfig]);

  // Initialize values and set up update timers
  useEffect(() => {
    mountedRef.current = true;

    if (!topicPaths || topicPaths.length === 0) {
      setValues(prev => Object.keys(prev).length === 0 ? prev : {});
      setRecentlyChanged(prev => prev.size === 0 ? prev : new Set());
      return;
    }

    // Initialize all values
    const initialValues = {};
    topicPaths.forEach((path) => {
      initialValues[path] = generateValue(path);
    });
    setValues(initialValues);

    // Set up random update timers for each topic
    topicPaths.forEach((path) => {
      const scheduleUpdate = () => {
        const config = simConfigMap.current[path];
        let delay;
        if (config?.updateIntervalMs) {
          const jitter = config.updateIntervalMs * 0.2;
          delay = config.updateIntervalMs + (Math.random() - 0.5) * 2 * jitter;
        } else {
          const lowerPath = path.toLowerCase();
          if (lowerPath.includes('oee') || lowerPath.includes('efficiency')) {
            delay = 15000 + Math.random() * 30000;
          } else if (lowerPath.includes('lot')) {
            delay = 20000 + Math.random() * 40000;
          } else if (lowerPath.includes('count') || lowerPath.includes('counter')) {
            delay = 2000 + Math.random() * 3000;
          } else {
            delay = 1000 + Math.random() * 4000;
          }
        }
        
        const timer = setTimeout(() => {
          if (!mountedRef.current) return;

          const newValue = generateValue(path);
          
          setValues((prev) => ({
            ...prev,
            [path]: newValue,
          }));
          
          setRecentlyChanged((prev) => {
            const newSet = new Set(prev);
            newSet.add(path);
            return newSet;
          });
          
          // Track the highlight cleanup timer so it can be cleared on unmount
          if (highlightTimersRef.current[path]) {
            clearTimeout(highlightTimersRef.current[path]);
          }
          highlightTimersRef.current[path] = setTimeout(() => {
            if (!mountedRef.current) return;
            setRecentlyChanged((prev) => {
              const newSet = new Set(prev);
              newSet.delete(path);
              return newSet;
            });
          }, 500);
          
          scheduleUpdate();
        }, delay);
        
        // Store one timer per path (overwrites previous, which has already fired)
        timersRef.current[path] = timer;
      };
      
      scheduleUpdate();
    });

    return () => {
      mountedRef.current = false;
      Object.values(timersRef.current).forEach(clearTimeout);
      timersRef.current = {};
      Object.values(highlightTimersRef.current).forEach(clearTimeout);
      highlightTimersRef.current = {};
      countersRef.current = {};
      baseValuesRef.current = {};
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicPathsKey, generateValue]);

  return {
    values,
    recentlyChanged,
    isConnected: true, // Always "connected" for simulation
  };
};

export default useSimulatedData;
